import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, ChevronRight, Languages, Clock, FileText } from "lucide-react";

export default function Questionnaires() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/questionnaires").then(r => setItems(r.data)); }, []);

  const filtered = items.filter(x =>
    !q || x.title.toLowerCase().includes(q.toLowerCase()) || (x.topic || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Library</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Quizzes</h1>
          <p className="text-slate-500 mt-1">Bilingual interactive quizzes for hospital staff training.</p>
        </div>
        <Link to="/questionnaires/new">
          <Button data-testid="create-quiz-btn" className="bg-sky-500 hover:bg-sky-600 rounded-xl">
            <Plus className="h-4 w-4 mr-1.5"/>New Quiz
          </Button>
        </Link>
      </header>

      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-100 flex items-center gap-3">
            <Search className="h-4 w-4 text-slate-400"/>
            <Input placeholder="Search by title or topic…" value={q} onChange={e=>setQ(e.target.value)}
              className="border-0 focus-visible:ring-0 shadow-none px-0" data-testid="quiz-search"/>
            <Badge variant="secondary" className="bg-slate-100 text-slate-600">{filtered.length} quizzes</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="quiz-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="py-3 px-4">Title</th>
                  <th className="py-3 px-4">Topic</th>
                  <th className="py-3 px-4">Questions</th>
                  <th className="py-3 px-4">Time / Q</th>
                  <th className="py-3 px-4">Languages</th>
                  <th className="py-3 px-4">Created</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-12 text-slate-400">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50"/>
                    No quizzes yet. <Link to="/questionnaires/new" className="text-sky-600 font-bold">Create your first quiz →</Link>
                  </td></tr>
                )}
                {filtered.map(quiz => (
                  <tr key={quiz.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                    <td className="py-3 px-4">
                      <Link to={`/questionnaires/${quiz.id}`} className="font-medium text-slate-900 hover:text-sky-700" data-testid={`quiz-row-${quiz.id}`}>{quiz.title}</Link>
                      {quiz.description_en && <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">{quiz.description_en}</div>}
                    </td>
                    <td className="py-3 px-4 text-slate-600">{quiz.topic || "—"}</td>
                    <td className="py-3 px-4 font-mono font-bold text-slate-900">{quiz.questions?.length || 0}</td>
                    <td className="py-3 px-4 text-slate-600 inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-slate-400"/>{quiz.time_per_q_sec}s</td>
                    <td className="py-3 px-4"><Badge variant="secondary" className="bg-sky-50 text-sky-700"><Languages className="h-3 w-3 mr-1"/>EN + हिं</Badge></td>
                    <td className="py-3 px-4 text-xs text-slate-500">{quiz.created_at ? new Date(quiz.created_at).toLocaleDateString() : "—"}</td>
                    <td className="py-3 px-4 text-right">
                      <Link to={`/questionnaires/${quiz.id}`}>
                        <Button variant="ghost" size="sm" data-testid={`quiz-open-${quiz.id}`}><ChevronRight className="h-4 w-4"/></Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
